import { getImageProps } from "next/image";
import Link from "next/link";
import { canOptimizeExploreImage } from "../_lib/explore-image";
import type { ExploreStoryCard } from "../types";
import { FeaturedStoryCard } from "./featured-story-card";
import { FreshStoryBreakingHero } from "./fresh-story-breaking-hero";
import { FreshStoryCompactItem } from "./fresh-story-compact-item";
import { FreshStorySidebarRow } from "./fresh-story-sidebar-row";
import { MobileSlotRenderer, slotKey } from "./mobile-slot-renderer";
import type { MobileSlot } from "./mobile-story-rhythm";
import { buildFreshStoriesMobileSlots } from "./mobile-story-rhythm";
import { SectionHeader } from "./section-header";

const TOP_GRID_COUNT = 3;
const MAX_SIDEBAR_ROWS = 4;
const GRID_COLS_MAP: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
};

interface TodayStoriesSectionProps {
  stories: ExploreStoryCard[];
  exploreBase: string;
}

export function TodayStoriesSection({
  stories,
  exploreBase,
}: TodayStoriesSectionProps) {
  /* ── Desktop layout mode ─────────────────────────────── */
  const hasConfirmedBreaking = stories.some(
    (s) => s.lifecycle === "BREAKING" && s.importance === "HIGH"
  );
  const useBreakingHeroLayout = stories.length === 3 && hasConfirmedBreaking;
  const useFeaturedSidebarLayout =
    !useBreakingHeroLayout && stories.length >= 7;

  /* ── Breaking hero layout slicing ────────────────────── */
  const breakingStory = useBreakingHeroLayout
    ? (stories.find(
        (s) => s.lifecycle === "BREAKING" && s.importance === "HIGH"
      ) ?? stories[0])
    : null;
  const breakingSideStories = useBreakingHeroLayout
    ? stories.filter((s) => s.storyId !== breakingStory?.storyId)
    : [];

  /* ── Grid + featured/sidebar slicing ─────────────────── */
  const gridStories = useBreakingHeroLayout
    ? []
    : stories.slice(0, TOP_GRID_COUNT);
  const featuredStory = useFeaturedSidebarLayout ? stories[3] : null;
  const sidebarStories = useFeaturedSidebarLayout
    ? stories.slice(4, 4 + MAX_SIDEBAR_ROWS)
    : [];
  /* When the sidebar can fit 3 or fewer rows (n=7 day), each row gets a
     2-line summary so it fills the height of the adjacent featured card.
     At 4 rows (n≥8) we keep the headline-only density. */
  const showSidebarSummary =
    useFeaturedSidebarLayout && sidebarStories.length <= 3;
  const listStories = getListStories({
    stories,
    useFeaturedSidebarLayout,
    useBreakingHeroLayout,
  });

  const showSeeAll = stories.length > TOP_GRID_COUNT;

  /* ── Desktop image preloading ────────────────────────── */
  const gridColsClass = GRID_COLS_MAP[gridStories.length] ?? "grid-cols-3";
  const compactCardImageSizes =
    gridStories.length === 2
      ? "(max-width: 1023px) 50vw, 450px"
      : "(max-width: 1023px) 33vw, 302px";

  const desktopLeadStory = getDesktopLeadStory({
    useBreakingHeroLayout,
    breakingStory,
    gridStories,
  });

  const desktopLeadImageSizes = useBreakingHeroLayout
    ? "(max-width: 1023px) 60vw, 540px"
    : compactCardImageSizes;
  const desktopLeadImageDimensions = useBreakingHeroLayout
    ? { height: 405, width: 540 }
    : { height: 170, width: 302 };

  const desktopLeadImageProps = desktopLeadStory?.coverImageUrl
    ? getImageProps({
        alt: desktopLeadStory.headline,
        height: desktopLeadImageDimensions.height,
        sizes: desktopLeadImageSizes,
        src: desktopLeadStory.coverImageUrl,
        unoptimized: !canOptimizeExploreImage({
          src: desktopLeadStory.coverImageUrl,
        }),
        width: desktopLeadImageDimensions.width,
      }).props
    : null;

  /* ── Mobile layout ───────────────────────────────────── */
  const mobileSlots = buildFreshStoriesMobileSlots({
    stories,
  });
  const priorityMobileSlot = mobileSlots.find(
    (slot) =>
      slot.variant === "hero" ||
      slot.variant === "image" ||
      slot.variant === "headline" ||
      slot.variant === "full-bleed"
  );
  const mobileLeadImagePreloadProps = getMobileLeadImagePreloadProps({
    slot: priorityMobileSlot,
  });

  return (
    <section>
      {desktopLeadImageProps ? (
        <link
          as="image"
          fetchPriority="high"
          href={desktopLeadImageProps.src}
          imageSizes={desktopLeadImageProps.sizes}
          imageSrcSet={desktopLeadImageProps.srcSet}
          media="(min-width: 640px)"
          rel="preload"
        />
      ) : null}
      {mobileLeadImagePreloadProps ? (
        <link
          as="image"
          fetchPriority="high"
          href={mobileLeadImagePreloadProps.src}
          imageSizes={mobileLeadImagePreloadProps.sizes}
          imageSrcSet={mobileLeadImagePreloadProps.srcSet}
          media="(max-width: 639px)"
          rel="preload"
        />
      ) : null}

      {/* Section header — hidden on mobile */}
      <div className="hidden sm:block">
        <SectionHeader
          seeAllHref={showSeeAll ? `${exploreBase}/stories` : undefined}
          seeAllLabel="See all →"
          title="Fresh stories"
        />
      </div>

      {/* Mobile: rhythm-based layout */}
      <div className="flex flex-col gap-4 px-1 sm:hidden">
        {mobileSlots.map((slot, idx) => (
          <MobileSlotRenderer
            exploreBase={exploreBase}
            key={slotKey({ slot, index: idx })}
            slot={slot}
          />
        ))}
        {showSeeAll && (
          <Link
            className="self-center py-2 font-medium text-[0.75rem] text-[var(--explore-text-muted)] uppercase tracking-[0.05em] no-underline transition-colors hover:text-[var(--explore-text-secondary)]"
            href={`${exploreBase}/stories`}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
          >
            See all stories →
          </Link>
        )}
      </div>

      {/* ── Desktop layouts ──────────────────────────────── */}
      <div className="hidden sm:block">
        {useBreakingHeroLayout && breakingStory ? (
          /* ── Breaking hero: large card left + smaller cards right ── */
          <div className="grid grid-cols-[3fr_2fr] gap-5 px-4 lg:px-0">
            <FreshStoryBreakingHero
              exploreBase={exploreBase}
              story={breakingStory}
            />
            <div className="flex flex-col gap-4">
              {breakingSideStories.map((story) => (
                <div className="flex flex-1" key={story.storyId}>
                  <FreshStoryCompactItem
                    exploreBase={exploreBase}
                    imageSizes="(max-width: 1023px) 40vw, 360px"
                    story={story}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* ── Top grid (shared by default + featured/sidebar) ── */}
            {gridStories.length === 1 ? (
              <div className="max-w-2xl px-4 lg:px-0">
                <FeaturedStoryCard
                  exploreBase={exploreBase}
                  story={gridStories[0]}
                />
              </div>
            ) : (
              <div className={`grid ${gridColsClass} gap-5 px-4 lg:px-0`}>
                {gridStories.map((story) => (
                  <FreshStoryCompactItem
                    exploreBase={exploreBase}
                    imageSizes={compactCardImageSizes}
                    key={story.storyId}
                    story={story}
                  />
                ))}
              </div>
            )}

            {useFeaturedSidebarLayout && featuredStory ? (
              /* ── Featured card left + compact sidebar rows right ── */
              <div className="mt-5 grid grid-cols-2 gap-5 px-4 lg:px-0">
                <FreshStoryCompactItem
                  exploreBase={exploreBase}
                  imageSizes="(max-width: 1023px) 50vw, 450px"
                  story={featuredStory}
                />
                <div className="flex flex-col gap-3">
                  {sidebarStories.map((story) => (
                    <FreshStorySidebarRow
                      exploreBase={exploreBase}
                      key={story.storyId}
                      showSummary={showSidebarSummary}
                      story={story}
                      stretch
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* ── Default: horizontal list for remaining stories ── */
              listStories.length > 0 && (
                <div className="mt-5 flex flex-col gap-4 px-4 lg:px-0">
                  {listStories.map((story) => (
                    <FeaturedStoryCard
                      exploreBase={exploreBase}
                      key={story.storyId}
                      story={story}
                    />
                  ))}
                </div>
              )
            )}

            {/* Overflow list for 9+ stories in featured/sidebar layout */}
            {useFeaturedSidebarLayout && listStories.length > 0 && (
              <div className="mt-5 flex flex-col gap-4 px-4 lg:px-0">
                {listStories.map((story) => (
                  <FeaturedStoryCard
                    exploreBase={exploreBase}
                    key={story.storyId}
                    story={story}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function getMobileLeadImagePreloadProps({
  slot,
}: {
  slot: MobileSlot | undefined;
}) {
  if (
    !slot ||
    (slot.variant !== "hero" &&
      slot.variant !== "image" &&
      slot.variant !== "headline" &&
      slot.variant !== "full-bleed")
  ) {
    return null;
  }

  const imageSizes =
    slot.variant === "full-bleed" ? "100vw" : "(max-width: 640px) 100vw, 50vw";
  const imageDimensions =
    slot.variant === "full-bleed"
      ? { height: 400, width: 600 }
      : { height: 360, width: 640 };

  return getImageProps({
    alt: slot.story.headline,
    height: imageDimensions.height,
    sizes: imageSizes,
    src: slot.story.coverImageUrl,
    unoptimized: !canOptimizeExploreImage({
      src: slot.story.coverImageUrl,
    }),
    width: imageDimensions.width,
  }).props;
}

function getListStories({
  stories,
  useFeaturedSidebarLayout,
  useBreakingHeroLayout,
}: {
  stories: ExploreStoryCard[];
  useFeaturedSidebarLayout: boolean;
  useBreakingHeroLayout: boolean;
}): ExploreStoryCard[] {
  if (useFeaturedSidebarLayout) {
    return stories.slice(4 + MAX_SIDEBAR_ROWS);
  }
  if (useBreakingHeroLayout) {
    return [];
  }
  return stories.slice(TOP_GRID_COUNT);
}

function getDesktopLeadStory({
  useBreakingHeroLayout,
  breakingStory,
  gridStories,
}: {
  useBreakingHeroLayout: boolean;
  breakingStory: ExploreStoryCard | null;
  gridStories: ExploreStoryCard[];
}): ExploreStoryCard | null {
  if (useBreakingHeroLayout) {
    return breakingStory;
  }
  if (gridStories.length > 1) {
    return gridStories[0];
  }
  return null;
}
